import { Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import * as fromActions from '@jepto/shared/store/client-connections/actions/client-connections.action';
import * as fromModels from '@jepto/shared/store/client-connections/models/client-connections.model';

import { Observable } from 'rxjs';
import { map, switchMap, delay, throwIfEmpty } from 'rxjs/operators';

import { pickBy } from 'lodash';
import { JepRequestService } from '@jepto/shared/services/jep-request/jep-request.service';
import {
	sentryLogEvent,
	sentryLogError,
	sentryLogSuccess,
} from '@jepto/shared/utils/collection';
import clientConnectionCreateMutation from '@jepto/features/settings/graphql/client-connections/mutations/mutation-client-connection-create';
import clientAutomationConnectionCreateMutation from '@jepto/features/settings/graphql/client-connections/mutations/mutation-client-automation-connection-create';
import getGoogleAnalyticsPropertiesQuery from '@jepto/features/settings/graphql/client-connections/queries/query-getGoogleAnalyticsProperties';
import getGoogleSearchConsolePropertiesQuery from '@jepto/features/settings/graphql/client-connections/queries/query-getGoogleSearchProperties';
import disconnectClientConnection from '@jepto/features/settings/graphql/client-connections/mutations/mutation-client-connection-delete';
import { ClientConnectionsService } from '@jepto/features/settings/services/client-connections.service';
import { JepCurrentAccountService } from '@jepto/shared/services/jep-current-account/jep-current-account.service';

@Injectable()
export class ClientConnectionsEffects {
	constructor(
		private actions$: Actions,
		private requestService: JepRequestService,
		private clientConnectionsService: ClientConnectionsService,
		private jepCurrentService: JepCurrentAccountService
	) {}

	disconnectClientConnection$: Observable<Action> = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					fromActions.ClientConnectionsActionTypes
						.DisconnectionClientConnect
				),
				map((action: any) => action.payload),
				switchMap(
					(
						payload: fromModels.IDisconnectClientConnectionPayload
					) => {
						sentryLogEvent('Disconnect Client Connect', payload);
						return this.requestService
							.mutate({
								mutation: disconnectClientConnection,
								variables: {
									sortKey: payload.sortKey,
									service: payload.service,
									userKey:
										this.jepCurrentService.getCognitoUserId(),
								},
							})
							.pipe(
								switchMap((result: any) => {
									console.log(
										'clientConnectionDelete',
										result
									);
									if (result?.errors && result.errors[0]) {
										sentryLogError(JSON.stringify(result));
										return this.clientConnectionsService.onClientConnectionTransactError(
											result.errors[0],
											'delete'
										);
									} else {
										sentryLogSuccess(JSON.stringify(result));
										return this.clientConnectionsService.onClientConnectionTransactSuccess(
											result.data[
												'clientConnectionDelete'
											],
											'delete'
										);
									}
								})
							);
					}
				)
			),
		{ dispatch: true }
	);

	clientConnectionRevert$: Observable<Action> = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					fromActions.ClientConnectionsActionTypes
						.RevertSharedClientConnection
				),
				delay(200),
				map((action: any) => action.payload),
				map(
					payload =>
						new fromActions.RevertSharedClientConnectionSuccess(
							payload
						)
				)
			),
		{ dispatch: true }
	);

	createClientConnection$: Observable<Action> = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromActions.ClientConnectionsActionTypes.Create),
				map((action: any) => action.payload),
				switchMap(
					(payload: fromModels.IClientConnectionCreatePayload) => {
						console.log('log payload', payload);
						sentryLogEvent('Create Client Connection', {
							input: pickBy({
								authId: payload.authId,
								clientId: payload.clientId,
								googleAnalytics: payload.googleAnalytics,
								googleAds: payload.googleAds,
								googleSearchConsole:
									payload.googleSearchConsole,
								googleMyBusiness: payload.googleMyBusiness,
								facebookAds: payload.facebookAds,
								microsoftAds: payload.microsoftAds,
								tiktokAds: payload.tiktokAds,
								linkedInAds: payload.linkedInAds,
								twitterAds: payload.twitterAds,
								instagramAds: payload.instagramAds,
								googleAnalytics4: payload.googleAnalytics4,
								userKey:
									this.jepCurrentService.getCognitoUserId(),
							}),
						});
						return this.requestService
							.mutate({
								mutation: clientConnectionCreateMutation,
								variables: {
									input: pickBy({
										authId: payload.authId,
										clientId: payload.clientId,
										googleAnalytics:
											payload.googleAnalytics,
										googleAds: payload.googleAds,
										googleSearchConsole:
											payload.googleSearchConsole,
										googleMyBusiness:
											payload.googleMyBusiness,
										facebookAds: payload.facebookAds,
										microsoftAds: payload.microsoftAds,
										tiktokAds: payload.tiktokAds,
										linkedInAds: payload.linkedInAds,
										twitterAds: payload.twitterAds,
										instagramAds: payload.instagramAds,
										googleAnalytics4:
											payload.googleAnalytics4,
										userKey:
											this.jepCurrentService.getCognitoUserId(),
									}),
								},
							})
							.pipe(
								switchMap((result: any) => {
									if (result?.errors && result.errors[0]) {
										sentryLogError(JSON.stringify(result));
										return this.clientConnectionsService.onClientConnectionTransactError(
											result.errors[0],
											'create'
										);
									} else {
										sentryLogSuccess(JSON.stringify(result));
										return this.clientConnectionsService.onClientConnectionTransactSuccess(
											result.data[
												'clientConnectionCreate'
											],
											'create'
										);
									}
								})
							);
					}
				)
			),
		{ dispatch: true }
	);

	createClientAutomationConnection$: Observable<Action> = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					fromActions.ClientConnectionsActionTypes.CreateAutomation
				),
				map((action: any) => action.payload),
				switchMap(
					(payload: fromModels.IClientConnectionCreatePayload) => {
						sentryLogEvent('Client Automation Connection Create', {
							input: pickBy({
								authId: payload.authId,
								clientId: payload.clientId,
								googleAnalytics: payload.googleAnalytics,
								googleAds: payload.googleAds,
								googleSearchConsole:
									payload.googleSearchConsole,
								googleMyBusiness: payload.googleMyBusiness,
								userKey:
									this.jepCurrentService.getCognitoUserId(),
							}),
						});
						return this.requestService
							.mutate({
								mutation:
									clientAutomationConnectionCreateMutation,
								variables: {
									input: pickBy({
										authId: payload.authId,
										clientId: payload.clientId,
										googleAnalytics:
											payload.googleAnalytics,
										googleAds: payload.googleAds,
										googleSearchConsole:
											payload.googleSearchConsole,
										googleMyBusiness:
											payload.googleMyBusiness,
										userKey:
											this.jepCurrentService.getCognitoUserId(),
									}),
								},
							})
							.pipe(
								switchMap((result: any) => {
									if (result?.errors && result.errors[0]) {
										sentryLogError(JSON.stringify(result));
										return this.clientConnectionsService.onClientConnectionTransactError(
											result.errors[0],
											'create'
										);
									} else {
										sentryLogSuccess(JSON.stringify(result));
										return this.clientConnectionsService.onClientConnectionTransactSuccess(
											result.data[
												'createAutomationConnection'
											],
											'create'
										);
									}
								})
							);
					}
				)
			),
		{ dispatch: true }
	);

	getGoogleSearchConsoleProperties$: Observable<Action> = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					fromActions.ClientConnectionsActionTypes
						.GetGoogleSearchConsoleProperties
				),
				map((action: any) => action.payload),
				switchMap(
					(
						payload: fromModels.IGoogleSearchConsolePropertiesPayload
					) => {
						return this.requestService
							.query({
								query: getGoogleSearchConsolePropertiesQuery,
								variables: {
									authId: payload.authId,
								},
							})
							.pipe(
								switchMap((result: any) => {
									if (result?.errors && result.errors[0]) {
										sentryLogError(JSON.stringify(result));
										return this.clientConnectionsService.onClientConnectionTransactError(
											result.errors[0],
											'getGAProperties'
										);
									} else {
										sentryLogSuccess(JSON.stringify(result));
										return this.clientConnectionsService.onClientConnectionTransactSuccess(
											{
												googleSearchConsoleProperties:
													result.data[
														'getGoogleSearchConsoleProperties'
													],
											},
											'getGSCProperties'
										);
									}
								})
							);
					}
				)
			),
		{ dispatch: true }
	);

	getGoogleAnalyticsProperties$: Observable<Action> = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					fromActions.ClientConnectionsActionTypes
						.GetGoogleAnalyticsProperties
				),
				map((action: any) => action.payload),
				switchMap(
					(
						payload: fromModels.IGetGoogleAnalyticsPropertiesPayload
					) => {
						sentryLogEvent('Get Google Analytics Properties', {
							accountId: payload.accountId,
							email: payload.email,
							authId: payload.authId,
							refresh: payload.refresh,
						});
						if (payload.actionType === 'getGAProperties') {
							return this.requestService
								.query({
									query: getGoogleAnalyticsPropertiesQuery,
									variables: {
										email: payload.email,
										authId: payload.authId,
										refresh: payload.refresh,
										userKey:
											this.jepCurrentService.getCognitoUserId(),
									},
								})
								.pipe(
									switchMap((result: any) => {
										if (
											result?.errors &&
											result.errors[0]
										) {
											sentryLogError(JSON.stringify(result));
											return this.clientConnectionsService.onClientConnectionTransactError(
												result.errors[0],
												'getGAProperties'
											);
										} else {
											sentryLogSuccess(JSON.stringify(result));
											return this.clientConnectionsService.onClientConnectionTransactSuccess(
												{
													googleAnalytics:
														result.data[
															'getGoogleAnalyticsProperties'
														],
												},
												'getGAProperties'
											);
										}
									})
								);
						} else {
							let q = `query ($authId: String, $email: String!, $refresh: Boolean, $userKey: String) {
								getGA4Properties(authId: $authId, email: $email, refresh:$refresh, userKey: $userKey) {
								accountId
								accountName
								properties{
									propertyId
									propertyName
								}
							}
							}`;

							return this.requestService
								.query({
									query: q,
									variables: {
										email: payload.email,
										authId: payload.authId,
										refresh: payload.refresh,
										userKey:
											this.jepCurrentService.getCognitoUserId(),
									},
								})
								.pipe(
									switchMap((result: any) => {
										if (
											result?.errors &&
											result.errors[0]
										) {
											sentryLogError(JSON.stringify(result));
											return this.clientConnectionsService.onClientConnectionTransactError(
												result.errors[0],
												'getGA4Properties'
											);
										} else {
											sentryLogSuccess(JSON.stringify(result));
											return this.clientConnectionsService.onClientConnectionTransactSuccess(
												{
													googleAnalytics:
														result.data[
															'getGA4Properties'
														],
												},
												'getGAProperties'
											);
										}
									})
								);
						}
					}
				)
			),
		{ dispatch: true }
	);
}
